import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { ComponentProps, useState, useMemo, ReactElement } from 'react';
import { Button } from 'app/agriculture/components/Button/Button';
import { SortableTableHead, HeadCell } from 'app/agriculture/components/Table/SortableTableHead';
import './SortableTable.scss';

export type Order = 'asc' | 'desc';

interface TableSort<DataType> {
    order: Order;
    key?: keyof DataType;
}

export interface TableDataProperty {
    display: string | number | ReactElement;
    value: string | number;
}

export interface TableDataType {
    [property: string]: TableDataProperty | number | string;
    id: string | number;
}

function getValueFromTableData(data: string | number | TableDataProperty) {
    return typeof data === 'string' || typeof data === 'number' ? data : data.value;
}

function getDisplayFromTableData(data: string | number | TableDataProperty) {
    return typeof data === 'string' || typeof data === 'number' ? data : data.display;
}

interface SortableTableProps<DataType extends TableDataType> {
    data: DataType[];
    headCells: HeadCell<DataType>[];
    defaultSort?: TableSort<DataType>;
    /** disables the internal sort functionality */
    disableSort?: boolean;
    button?: (row: DataType) => ComponentProps<typeof Button> | null;
    /**
     * Callback called whenever the table is sorted
     * @param orderBy the data key that is being sorted by
     * @param order the order of the sort action
     */
    onSort?: (orderBy: keyof DataType, order: Order) => void;
}

function descendingComparator<DataType extends TableDataType>(
    a: DataType,
    b: DataType,
    orderBy: keyof DataType,
) {
    const sortableA = getValueFromTableData(a[orderBy]);
    const sortableB = getValueFromTableData(b[orderBy]);

    if (sortableA > sortableB) {
        return -1;
    }
    if (sortableA < sortableB) {
        return 1;
    }
    return 0;
}
function getComparator<DataType extends TableDataType>(
    order: Order,
    orderBy: keyof DataType,
): (a: DataType, b: DataType) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

export default function SortableTable<DataType extends TableDataType>({
    data,
    headCells,
    defaultSort = { order: 'asc' },
    button,
    disableSort = false,
    onSort = () => {
        /* do nothing */
    },
}: SortableTableProps<DataType>) {
    const [order, setOrder] = useState<Order>(defaultSort.order);
    const [orderBy, setOrderBy] = useState<keyof DataType>(defaultSort.key ?? headCells[0].key);

    const handleRequestSort = (property: keyof DataType) => {
        const isAsc = orderBy === property && order === 'asc';
        const sortOrder = isAsc ? 'desc' : 'asc';
        onSort(property, sortOrder);
        setOrder(sortOrder);
        setOrderBy(property);
    };

    const visibleRows = useMemo(() => {
        if (disableSort) return data;
        return data.slice().sort(getComparator(order, orderBy));
    }, [order, orderBy, data, disableSort]);

    return (
        <Table className="SortableTable" aria-labelledby="tableTitle" size="medium">
            <SortableTableHead
                headCells={headCells}
                hasButton={!!button}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={data.length}
                disableSort={disableSort}
            />
            <TableBody>
                {visibleRows.map((row, rowIndex) => {
                    const labelId = `sortable-table-row-${rowIndex}`;
                    const buttonProps = button?.(row);

                    return (
                        <TableRow hover id={labelId} key={row.id} data-testid="SortableTableRow">
                            {headCells.map(({ align, key, label, isHidden }, cellIndex) => {
                                const display = getDisplayFromTableData(row[key]);
                                if (isHidden !== true) {
                                    return (
                                        <TableCell
                                            align={align ?? 'left'}
                                            data-testid={`table-row-${rowIndex}-cell-${cellIndex}`}
                                            key={`${row.id}--${label}--value`}
                                        >
                                            <p>{display}</p>
                                        </TableCell>
                                    );
                                }
                                return null;
                            })}
                            {!!button && (
                                <TableCell align="right">
                                    {!!buttonProps && <Button {...buttonProps} />}
                                </TableCell>
                            )}
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}
